// ============================================================
// ConditionBadge - one pressable condition in the Add-Condition picker
// ============================================================
// Coloured by the condition's own ramp, greyed when the target is immune,
// with a lock icon the GM presses to override that immunity. What a press
// DOES is the picker's decision (`badgeAction`); the badge only reports it.

import type { MouseEvent } from 'react';
import { condColor } from '../engine/conditions';
import type { EligibleCondition } from '../engine/conditionEligibility';
import { onActivateKey } from '../utils/keyActivation';
import ConditionIcon from './ConditionIcon.jsx';
import styles from '../styles/AddConditionModal.module.css';
import clsx from '../utils/clsx';

interface ConditionBadgeProps {
  cond: EligibleCondition;
  /** The GM unlocked this condition's immunity. */
  overridden: boolean;
  /** A press; `shiftKey` is the one-off immunity override. */
  onPress: (shiftKey: boolean) => void;
  onToggleLock: () => void;
}

/** A condition badge. `data-testid="cond-badge-<code>"` is clicked by E2E. */
export function ConditionBadge({ cond, overridden, onPress, onToggleLock }: ConditionBadgeProps) {
  const cc = condColor(cond.code);
  const isImmune = cond.immune && !overridden;
  return (
    <span
      className={clsx(
        styles.condBadge,
        isImmune && styles.condBadgeImmune,
        cond.immune && overridden && styles.condBadgeOverridden,
      )}
      style={{ background: cc.bg, borderColor: cc.border, color: cc.text }}
      role="button"
      tabIndex={0}
      aria-disabled={isImmune || undefined}
      title={isImmune ? `Immune: ${cond.immuneSources.join(', ')} (Shift+click to override)` : cond.name}
      onClick={(e) => onPress(e.shiftKey)}
      onKeyDown={onActivateKey(() => onPress(false))}
      data-testid={`cond-badge-${cond.code}`}
    >
      <ConditionIcon code={cond.code} size={14} onClick={undefined} />
      {cond.name}
      {cond.immune && <ImmunityLock overridden={overridden} onToggle={onToggleLock} />}
    </span>
  );
}

/**
 * The lock inside an immune badge. Its press must not ALSO press the badge
 * around it, so the click and the activating key stop there; any other key
 * (Escape, say) still reaches the dialog.
 */
function ImmunityLock({ overridden, onToggle }: { overridden: boolean; onToggle: () => void }) {
  const onClick = (e: MouseEvent) => {
    e.stopPropagation();
    onToggle();
  };
  return (
    <span
      className={styles.lockIcon}
      onClick={onClick}
      onKeyDown={(e) =>
        onActivateKey(() => {
          e.stopPropagation();
          onToggle();
        })(e)
      }
      tabIndex={0}
      title={overridden ? 'Re-lock (restore immunity)' : 'Unlock (GM override)'}
      role="button"
      aria-label={overridden ? 'Re-lock condition' : 'Override immunity'}
    >
      {overridden ? '🔓' : '🔒'}
    </span>
  );
}
